export interface EventTemplateProps {
  type: 'confirm' | 'cancel' | 'link' | 'follow_up' | 'reminder';
  clientName: string;
  content: string;
  businessName: string;
  businessPhone?: string;
  businessEmail?: string;
  event: {
    title: string;
  }
}

export const EMAIL_QUERY_CONFIG = {
  PENDING_REFRESH_INTERVAL: 10000, // 10 seconds
  SENT_REFRESH_INTERVAL: 30000,    // 30 seconds  
  STATS_REFRESH_INTERVAL: 10000,   // 10 seconds
  STALE_TIME_SHORT: 5000,          // 5 seconds
  STALE_TIME_MEDIUM: 10000,        // 10 seconds
  STALE_TIME_LONG: 30000,          // 30 seconds
  STALE_TIME_EXTRA_LONG: 60000,    // 1 minute
} as const;


// Default prompt template
export const getDefaultPrompt = () => `Important guidelines:
- Keep the email under 200 words
- Use proper formatting with paragraphs
- Be specific about dates, times, and commitments mentioned
- Address the recipient by name if mentioned in the conversation

MANDATORY - NO PLACEHOLDERS:
- NEVER use placeholder text like "[Your Name]", "[Your Position]", "[Business Name]", "[Date]", "[Client Name]", or any other bracketed placeholders
- ALWAYS use real information from the transcript, not placeholder text
- If you need to sign the email, use the business name instead of personal name placeholders

DATE HANDLING RULES:
- If specific dates/times are mentioned in the transcript, use the EXACT dates/times from the conversation
- If no specific dates are mentioned, DO NOT use placeholder dates like "[Date]", "[Time]", "TBD", or "To be determined"
- If dates are unclear or not specified, simply omit date references or use general terms like "soon", "in the near future", "as discussed"
- NEVER invent or assume dates that weren't explicitly mentioned in the conversation`;


export const eventTemplate = ({type, clientName, content, businessName, businessPhone, businessEmail, event}: EventTemplateProps) => {
  const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const titles = {
    confirm: event.title ? `✓ ${event.title} Confirmed` : '✓ Appointment Confirmed',
    cancel: event.title ? `✗ ${event.title} Cancelled` : '✗ Appointment Cancelled',
    link: event.title ? `✓ ${event.title} Confirmation` : '✓ Appointment Confirmation',
    follow_up: event.title ? `${event.title} Follow Up` : 'Follow Up Info',
    reminder: event.title ? `🔔 ${event.title} Reminder` : '🔔 Reminder',
  }
  const subTitles = {
    confirm: event.title ? `Your ${event.title} successfully confirmed.` : 'Your appointment successfully confirmed.',
    cancel: event.title ? `Your ${event.title} has been successfully cancelled.` : 'Your appointment has been successfully cancelled.',
    link: event.title ? `Please finalize your ${event.title} by clicking the link below:` : 'Please finalize your appointment by clicking the link below:',
    follow_up: event.title ? `We're happy to share the details you asked for:` : 'We\'re happy to share the details you asked for:',
    reminder: event.title ? `This is a friendly reminder about your upcoming ${event.title}.` : 'This is a friendly reminder about your upcoming appointment.',
  }
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>
    ${titles[type]}
  </title>
</head>
<body style="margin: 0; padding: 20px; font-family: Arial, sans-serif; background-color: #ffffff;">
  <div style="max-width: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); padding: 40px 30px; border: 1px solid #e0e0e0;">
      
      <div style="text-align: center; margin-bottom: 30px;">
          <h1 style="color: ${type === 'cancel' ? '#e74c3c' : '#2c3e50'}; margin: 0; font-size: 28px;">
              ${titles[type]}
          </h1>
      </div>
      
      <p style="font-size: 16px; color: #34495e; margin-bottom: 20px;">
          Welcome, <strong>${clientName}</strong>!
      </p>
      
      <p style="font-size: 16px; color: #34495e; margin-bottom: 30px;">
              ${subTitles[type]}
      </p>
      
      <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin-bottom: 30px;">${content}</div>
      
      <p style="font-size: 16px; color: #34495e; margin-bottom: 20px;">
          ${type === 'confirm' 
            ? "If you'll be busy — no problem. Just let us know and we'll find another convenient time." 
            : type === 'cancel' 
            ? "We apologize for any inconvenience. If you'd like to reschedule, we'll be happy to find a new convenient time for you." 
            : type === 'link'
            ? "You can pick a time and date for your appointment at the link." 
            : "    If you have any questions or need further assistance, feel free to let us know." 
            }
      </p>
      
      <p style="font-size: 16px; color: #34495e;">
          Contact us: ${businessPhone ? `<strong>${businessPhone}</strong>` : ''}${businessPhone && businessEmail ? ' or ' : ''}${businessEmail ? `email <strong>${businessEmail}</strong>` : ''}
      </p>
      
      <div style="text-align: center; margin-top: 30px;">
          <p style="font-size: 14px; color: #7f8c8d;">
              Team of ${businessName}<br>
              ${today}
          </p>
      </div>
      
  </div>
</body>
</html>`;
};